import React from 'react';
import clsx from 'clsx';
import { ListItemButton, type ListItemButtonProps, ListItemText, alpha, styled } from '@mui/material';
import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import { Icon } from 'app/main/components';
import FuseNavBadge from '../../FuseNavBadge';
import { type FuseNavItemType } from '../../types/FuseNavItemType';

interface RootProps extends ListItemButtonProps {
	itemPadding: number;
	to?: string;
	activeClassName: string;
	end?: boolean;
}

const Root = styled(ListItemButton, { shouldForwardProp: name => name !== 'itemPadding' })<RootProps>(
	({ theme, itemPadding }) => ({
		height: 40,
		width: '100%',
		borderRadius: '6px',
		margin: '0 0 4px 0',
		paddingRight: 12,
		paddingLeft: itemPadding,
		color: alpha(theme.palette.text.primary, 0.7),
		cursor: 'pointer',
		textDecoration: 'none!important',
		'&:hover': {
			color: theme.palette.text.primary
		},
		'&.active': {
			color: theme.palette.text.primary,
			backgroundColor:
				theme.palette.mode === 'light' ? 'rgba(0, 0, 0, .05)!important' : 'rgba(255, 255, 255, .1)!important',
			pointerEvents: 'none',
			transition: 'border-radius .15s cubic-bezier(0.4,0.0,0.2,1)',
			'& > .fuse-list-item-text-primary': {
				color: 'inherit'
			},
			'& > .fuse-list-item-icon': {
				color: 'inherit'
			}
		},
		'& >.fuse-list-item-icon': {
			marginRight: 12,
			color: 'inherit'
		},
		'& > .fuse-list-item-text': {}
	})
);

interface FuseNavVerticalItemProps {
	onItemClick: (item: FuseNavItemType) => void;
	nestedLevel: number;
	item: FuseNavItemType;
}

function FuseNavVerticalItem(props: FuseNavVerticalItemProps) {
	const { item, nestedLevel, onItemClick } = props;

	const itemPadding = nestedLevel > 0 ? 28 + nestedLevel * 16 : 12;

	return React.useMemo(
		() => (
			<Root
				component={NavLinkAdapter}
				to={item.url}
				activeClassName="active"
				className="fuse-list-item"
				onClick={() => onItemClick && onItemClick(item)}
				end={item.end}
				itemPadding={itemPadding}
				role="button"
			>
				{item.icon && (
					<Icon
						icon={item.icon}
						className={clsx('fuse-list-item-icon mr-12 shrink-0 text-20', item.iconClass)}
						color="action"
					/>
				)}

				<ListItemText
					className="fuse-list-item-text"
					primary={item.title}
					classes={{ primary: 'text-13 font-medium fuse-list-item-text-primary' }}
				/>
				{item.badge && <FuseNavBadge badge={item.badge} />}
			</Root>
		),
		[item, itemPadding, onItemClick]
	);
}

const NavVerticalItem = FuseNavVerticalItem;

export default NavVerticalItem;
